import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAgent } from '../../Context/AgentContext';
import { useAuth } from '../../Context/UnifiedAuthContext';
import HeadingForAll from '../../Components/HeadingForAll/HeadingForAll';
import { FaPlus } from 'react-icons/fa6';

const AgentRegistration = () => {
  const navigate = useNavigate();
  const { registerAgent, loading, error, clearError } = useAgent();
  const { user, isAuthenticated, isAgent, logout } = useAuth();
  
  // Form states
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', or null
  
  // Registration form
  const [registerForm, setRegisterForm] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    agent_type: 'individual',
    company: '',
    phone: '',
    business_address: '',
    bio: '',
    license_number: '',
    years_experience: '',
    specializations: [],
    service_areas: [],
    certifications: [],
    languages_spoken: [],
    office_hours: '',
    professional_summary: '',
    website: ''
  });

  // Heading data
  const headingForAllData = {
    subheading: "Join Our Network",
    heading: "Agent Registration"
  };

  // Validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePassword = (password) => {
    // At least 8 characters, 1 uppercase, 1 lowercase, 1 number
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d@$!%*?&]{8,}$/;
    return passwordRegex.test(password);
  };

  const validatePhone = (phone) => {
    // Must start with +81 for Japan
    const phoneRegex = /^\+81[0-9]{10,11}$/;
    return phoneRegex.test(phone.replace(/[\s\-\(\)]/g, ''));
  };

  const validateURL = (url) => {
    if (!url) return true; // Optional field
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const validateForm = () => {
    const errors = {};

    // Name validation
    if (!registerForm.name.trim()) {
      errors.name = 'Full name is required';
    } else if (registerForm.name.trim().length < 2) {
      errors.name = 'Name must be at least 2 characters';
    }

    // Email validation
    if (!registerForm.email.trim()) {
      errors.email = 'Email is required';
    } else if (!validateEmail(registerForm.email)) {
      errors.email = 'Please enter a valid email address';
    }

    // Password validation
    if (!registerForm.password) {
      errors.password = 'Password is required';
    } else if (!validatePassword(registerForm.password)) {
      errors.password = 'Password must be at least 8 characters with uppercase, lowercase, and number';
    }

    // Confirm password validation
    if (!registerForm.confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (registerForm.password !== registerForm.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    // Phone validation
    if (!registerForm.phone.trim()) {
      errors.phone = 'Phone number is required';
    } else if (!validatePhone(registerForm.phone)) {
      errors.phone = 'Please enter a valid Japanese phone number starting with +81';
    }

    // Business address validation
    if (!registerForm.business_address.trim()) {
      errors.business_address = 'Business address is required';
    } else if (registerForm.business_address.trim().length < 10) {
      errors.business_address = 'Please provide a complete address';
    }

    // Company name validation for companies
    if (registerForm.agent_type === 'company' && !registerForm.company.trim()) {
      errors.company = 'Company name is required for companies';
    }

    // Website validation
    if (registerForm.website && !validateURL(registerForm.website)) {
      errors.website = 'Please enter a valid URL (e.g., https://example.com)';
    }

    // Years of experience validation
    if (registerForm.years_experience && (registerForm.years_experience < 0 || registerForm.years_experience > 70)) {
      errors.years_experience = 'Please enter a valid number of years (0-70)';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle field blur for real-time validation
  const handleFieldBlur = (fieldName) => {
    setTouched(prev => ({ ...prev, [fieldName]: true }));
    validateForm();
  };

  // Handle input change
  const handleInputChange = (fieldName, value) => {
    setRegisterForm(prev => ({ ...prev, [fieldName]: value }));
    
    // Clear specific field error when user starts typing
    if (validationErrors[fieldName]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  // Check authentication status on component mount
  useEffect(() => {
    if (isAuthenticated) {
      if (isAgent) {
        navigate('/agent/dashboard');
      }
    }
  }, [isAuthenticated, isAgent, navigate]);

  // Handle logout for users who need to log out first
  const handleLogout = () => {
    logout();
  };

  // If user is logged in but not an agent, show not allowed message
  if (isAuthenticated && !isAgent) {
    return (
      <div className="min-h-screen bg-[#fafafa] flex items-center justify-center px-4">
        <div className="max-w-2xl w-full bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-red-500 to-red-600 px-8 py-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-white mb-2">Access Restricted</h1>
                <p className="text-white text-sm">Agent registration only</p>
              </div>
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <span className="text-white text-xl font-bold">!</span>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-8 text-center">
            <div className="mb-6">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl text-red-600 font-bold">!</span>
              </div>
              <h2 className="text-xl font-semibold text-gray-800 mb-2">
                User Account Detected
              </h2>
              <p className="text-gray-600 leading-relaxed">
                You are currently logged in as a regular user. To register as an agent, 
                please log out of your current account first.
              </p>
            </div>

            <div className="space-y-3">
              <button
                onClick={handleLogout}
                className="w-full bg-red-500 hover:bg-red-600 text-white py-3 px-4 rounded-lg font-semibold transition-colors duration-200"
              >
                Log Out and Register as Agent
              </button>
              
              <button
                onClick={() => navigate('/')}
                className="w-full bg-gray-500 hover:bg-gray-600 text-white py-3 px-4 rounded-lg font-semibold transition-colors duration-200"
              >
                Return to Home
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // If already an agent, show loading message
  if (isAuthenticated && isAgent) {
    return (
      <div className="min-h-screen bg-[#fafafa] flex items-center justify-center px-4">
        <div className="text-center">
          <div className="w-16 h-16 bg-[#10c9c0] rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
            <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
          </div>
          <p className="text-gray-600 font-medium">Redirecting to agent dashboard...</p>
        </div>
      </div>
    );
  }

  // Form handlers
  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setSubmitStatus(null);
    clearError();
    
    // Validate form
    if (!validateForm()) {
      setFormLoading(false);
      setSubmitStatus('error');
      // Mark all fields as touched to show validation errors
      const allFields = Object.keys(registerForm);
      const touchedFields = {};
      allFields.forEach(field => {
        touchedFields[field] = true;
      });
      setTouched(touchedFields);
      return;
    }
    
    try {
      await registerAgent({
        name: registerForm.name.trim(),
        email: registerForm.email.trim().toLowerCase(),
        password: registerForm.password,
        agent_type: registerForm.agent_type,
        company: registerForm.company.trim(),
        phone: registerForm.phone.trim(),
        business_address: registerForm.business_address.trim(),
        bio: registerForm.bio.trim(),
        license_number: registerForm.license_number.trim(),
        years_experience: parseInt(registerForm.years_experience) || 0,
        specializations: registerForm.specializations,
        service_areas: registerForm.service_areas,
        certifications: registerForm.certifications,
        languages_spoken: registerForm.languages_spoken,
        office_hours: registerForm.office_hours.trim(),
        professional_summary: registerForm.professional_summary.trim(),
        website: registerForm.website.trim()
      });
      
      setSubmitStatus('success');
      
      // Wait a bit to show success message, then navigate
      setTimeout(() => {
        // Force a page refresh after navigation to ensure proper data loading
        window.location.href = '/agent/dashboard';
      }, 2000);
      
    } catch (error) {
      console.error('Registration failed:', error);
      setSubmitStatus('error');
    } finally {
      setFormLoading(false);
    }
  };

  const addSpecialization = (specialization) => {
    if (specialization.trim() && !registerForm.specializations.includes(specialization.trim())) {
      setRegisterForm(prev => ({
        ...prev,
        specializations: [...prev.specializations, specialization.trim()]
      }));
    }
  };

  const removeSpecialization = (index) => {
    setRegisterForm(prev => ({
      ...prev,
      specializations: prev.specializations.filter((_, i) => i !== index)
    }));
  };

  const addServiceArea = (area) => {
    if (area.trim() && !registerForm.service_areas.includes(area.trim())) {
      setRegisterForm(prev => ({
        ...prev,
        service_areas: [...prev.service_areas, area.trim()]
      }));
    }
  };

  const removeServiceArea = (index) => {
    setRegisterForm(prev => ({
      ...prev,
      service_areas: prev.service_areas.filter((_, i) => i !== index)
    }));
  };

  const addCertification = (certification) => {
    if (certification.trim() && !registerForm.certifications.includes(certification.trim())) {
      setRegisterForm(prev => ({
        ...prev,
        certifications: [...prev.certifications, certification.trim()]
      }));
    }
  };

  const removeCertification = (index) => {
    setRegisterForm(prev => ({
      ...prev,
      certifications: prev.certifications.filter((_, i) => i !== index)
    }));
  };

  const addLanguage = (language) => {
    if (language.trim() && !registerForm.languages_spoken.includes(language.trim())) {
      setRegisterForm(prev => ({
        ...prev,
        languages_spoken: [...prev.languages_spoken, language.trim()]
      }));
    }
  };

  const removeLanguage = (index) => {
    setRegisterForm(prev => ({
      ...prev,
      languages_spoken: prev.languages_spoken.filter((_, i) => i !== index)
    }));
  };

  // Helper component for error display
  const ErrorMessage = ({ error, touched }) => {
    if (!error || !touched) return null;
    return (
      <div className="absolute left-0 right-0 top-full mt-1 z-10">
        <p className="text-red-600 text-sm bg-red-50 border border-red-200 rounded px-2 py-1 shadow-sm">
          {error}
        </p>
      </div>
    );
  };

  // Main registration form (shown when no user is logged in)
  return (
    <div className="min-h-screen bg-[#fafafa]">
      <div className="flex flex-col">
        {/* Header Component */}
        <div className="mb-12">
          <HeadingForAll
            smallHeading={headingForAllData.subheading}
            bigHeading={headingForAllData.heading}
          />
        </div>

        {/* Registration Form Container - Increased width */}
        <div className="max-w-7xl mx-auto bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden px-4 sm:px-6 lg:px-8 mb-10">
          {/* Success/Error Status Display */}
          {submitStatus === 'success' && (
            <div className="mx-8 mt-6 bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center">
                <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center mr-3">
                  <span className="text-white text-sm">✓</span>
                </div>
                <div>
                  <p className="text-green-800 font-medium">Registration Successful!</p>
                  <p className="text-green-600 text-sm">Redirecting to your dashboard...</p>
                </div>
              </div>
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="mx-8 mt-6 bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center">
                <span className="text-red-500 mr-2 text-lg font-bold">×</span>
                <span className="text-red-700">Please fix the validation errors below and try again.</span>
              </div>
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="mx-8 mt-6 bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <span className="text-red-500 mr-2 text-lg font-bold">×</span>
                  <span className="text-red-700">{error}</span>
                </div>
                <button 
                  onClick={clearError}
                  className="text-red-500 hover:text-red-700 text-lg font-bold"
                >
                  ×
                </button>
              </div>
            </div>
          )}

          {/* Registration Form */}
          <div className="p-8 lg:p-12">
            <form onSubmit={handleRegisterSubmit} className="space-y-10">
              {/* Personal Information */}
              <div className="space-y-8">
                <h4 className="text-xl font-semibold text-gray-800 border-b-2 border-[#10c9c0] pb-4">
                  Personal Information
                </h4>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8">
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      value={registerForm.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      onBlur={() => handleFieldBlur('name')}
                      className={`w-full p-4 border rounded-lg focus:ring-2 focus:ring-[#10c9c0] focus:border-transparent transition-colors ${
                        validationErrors.name && touched.name ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Enter your full name"
                      required
                    />
                    <ErrorMessage error={validationErrors.name} touched={touched.name} />
                  </div>

                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      value={registerForm.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      onBlur={() => handleFieldBlur('email')}
                      className={`w-full p-4 border rounded-lg focus:ring-2 focus:ring-[#10c9c0] focus:border-transparent transition-colors ${
                        validationErrors.email && touched.email ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Enter your email"
                      required
                    />
                    <ErrorMessage error={validationErrors.email} touched={touched.email} />
                  </div>

                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      value={registerForm.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      onBlur={() => handleFieldBlur('phone')}
                      className={`w-full p-4 border rounded-lg focus:ring-2 focus:ring-[#10c9c0] focus:border-transparent transition-colors ${
                        validationErrors.phone && touched.phone ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="+81 90 1234 5678"
                      required
                    />
                    <ErrorMessage error={validationErrors.phone} touched={touched.phone} />
                  </div>

                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Password *
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={registerForm.password}
                        onChange={(e) => handleInputChange('password', e.target.value)}
                        onBlur={() => handleFieldBlur('password')}
                        className={`w-full p-4 border rounded-lg focus:ring-2 focus:ring-[#10c9c0] focus:border-transparent pr-12 transition-colors ${
                          validationErrors.password && touched.password ? 'border-red-300' : 'border-gray-300'
                        }`}
                        placeholder="Create a password"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-4 top-4 text-gray-500 hover:text-gray-700 text-sm"
                      >
                        {showPassword ? 'Hide' : 'Show'}
                      </button>
                    </div>
                    <ErrorMessage error={validationErrors.password} touched={touched.password} />
                  </div>

                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Confirm Password *
                    </label>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={registerForm.confirmPassword}
                        onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                        onBlur={() => handleFieldBlur('confirmPassword')}
                        className={`w-full p-4 border rounded-lg focus:ring-2 focus:ring-[#10c9c0] focus:border-transparent pr-12 transition-colors ${
                          validationErrors.confirmPassword && touched.confirmPassword ? 'border-red-300' : 'border-gray-300'
                        }`}
                        placeholder="Confirm your password"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-4 top-4 text-gray-500 hover:text-gray-700 text-sm"
                      >
                        {showConfirmPassword ? 'Hide' : 'Show'}
                      </button>
                    </div>
                    <ErrorMessage error={validationErrors.confirmPassword} touched={touched.confirmPassword} />
                  </div>

                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Agent Type *
                    </label>
                    <select
                      value={registerForm.agent_type}
                      onChange={(e) => handleInputChange('agent_type', e.target.value)}
                      className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#10c9c0] focus:border-transparent"
                      required
                    >
                      <option value="individual">Individual</option>
                      <option value="company">Company</option>
                    </select>
                  </div>
                </div>

                {registerForm.agent_type === 'company' && (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div className="relative">
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Company Name *
                      </label>
                      <input
                        type="text"
                        value={registerForm.company}
                        onChange={(e) => handleInputChange('company', e.target.value)}
                        onBlur={() => handleFieldBlur('company')}
                        className={`w-full p-4 border rounded-lg focus:ring-2 focus:ring-[#10c9c0] focus:border-transparent transition-colors ${
                          validationErrors.company && touched.company ? 'border-red-300' : 'border-gray-300'
                        }`}
                        placeholder="Your company name"
                        required
                      />
                      <ErrorMessage error={validationErrors.company} touched={touched.company} />
                    </div>
                  </div>
                )}

                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Business Address *
                  </label>
                  <textarea
                    value={registerForm.business_address}
                    onChange={(e) => handleInputChange('business_address', e.target.value)}
                    onBlur={() => handleFieldBlur('business_address')}
                    className={`w-full p-4 border rounded-lg focus:ring-2 focus:ring-[#10c9c0] focus:border-transparent transition-colors ${
                      validationErrors.business_address && touched.business_address ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Your complete business address"
                    rows="3"
                    required
                  />
                  <ErrorMessage error={validationErrors.business_address} touched={touched.business_address} />
                </div>
              </div>

              {/* Professional Information */}
              <div className="space-y-8">
                <h4 className="text-xl font-semibold text-gray-800 border-b-2 border-[#10c9c0] pb-4">
                  Professional Information
                </h4>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8">
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      License Number
                    </label>
                    <input
                      type="text"
                      value={registerForm.license_number}
                      onChange={(e) => handleInputChange('license_number', e.target.value)}
                      className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#10c9c0] focus:border-transparent"
                      placeholder="Your license number"
                    />
                  </div>

                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Years of Experience
                    </label>
                    <input
                      type="number"
                      value={registerForm.years_experience}
                      onChange={(e) => handleInputChange('years_experience', e.target.value)}
                      onBlur={() => handleFieldBlur('years_experience')}
                      className={`w-full p-4 border rounded-lg focus:ring-2 focus:ring-[#10c9c0] focus:border-transparent transition-colors ${
                        validationErrors.years_experience && touched.years_experience ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Years in real estate"
                      min="0"
                      max="70"
                    />
                    <ErrorMessage error={validationErrors.years_experience} touched={touched.years_experience} />
                  </div>

                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Website
                    </label>
                    <input
                      type="url"
                      value={registerForm.website}
                      onChange={(e) => handleInputChange('website', e.target.value)}
                      onBlur={() => handleFieldBlur('website')}
                      className={`w-full p-4 border rounded-lg focus:ring-2 focus:ring-[#10c9c0] focus:border-transparent transition-colors ${
                        validationErrors.website && touched.website ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="https://yourwebsite.com"
                    />
                    <ErrorMessage error={validationErrors.website} touched={touched.website} />
                  </div>

                  <div className="lg:col-span-2 xl:col-span-3 relative">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Office Hours
                    </label>
                    <input
                      type="text"
                      value={registerForm.office_hours}
                      onChange={(e) => handleInputChange('office_hours', e.target.value)}
                      className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#10c9c0] focus:border-transparent"
                      placeholder="Mon-Fri 9AM-6PM, Sat 10AM-4PM"
                    />
                  </div>
                </div>

                <div className="">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Professional Bio
                    </label>
                    <textarea
                      value={registerForm.bio}
                      onChange={(e) => handleInputChange('bio', e.target.value)}
                      className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#10c9c0] focus:border-transparent"
                      placeholder="Tell potential clients about yourself and your expertise and your company..."
                      rows="5"
                    />
                  </div>


                </div>
              </div>

              {/* Specializations */}
              <div className="space-y-8">
                <h4 className="text-xl font-semibold text-gray-800 border-b-2 border-[#10c9c0] pb-4">
                  Specializations
                </h4>
                <TagInput
                  items={registerForm.specializations}
                  onAdd={addSpecialization}
                  onRemove={removeSpecialization}
                  placeholder="Add specialization (e.g., Residential, Commercial, Investment Properties)"
                />
              </div>

              {/* Service Areas */}
              <div className="space-y-8">
                <h4 className="text-xl font-semibold text-gray-800 border-b-2 border-[#10c9c0] pb-4">
                  Service Areas
                </h4>
                <TagInput
                  items={registerForm.service_areas}
                  onAdd={addServiceArea}
                  onRemove={removeServiceArea}
                  placeholder="Add service area (e.g., Tokyo, Shibuya, Shinjuku)"
                />
              </div>

              {/* Certifications */}
              <div className="space-y-8">
                <h4 className="text-xl font-semibold text-gray-800 border-b-2 border-[#10c9c0] pb-4">
                  Certifications
                </h4>
                <TagInput
                  items={registerForm.certifications}
                  onAdd={addCertification}
                  onRemove={removeCertification}
                  placeholder="Add certification (e.g., Real Estate License, Property Management)"
                />
              </div>

              {/* Languages */}
              <div className="space-y-8">
                <h4 className="text-xl font-semibold text-gray-800 border-b-2 border-[#10c9c0] pb-4">
                  Languages Spoken
                </h4>
                <TagInput
                  items={registerForm.languages_spoken}
                  onAdd={addLanguage}
                  onRemove={removeLanguage}
                  placeholder="Add language (e.g., Japanese, English, Mandarin)"
                />
              </div>

              <div className="pt-8">
                <button
                  type="submit"
                  disabled={formLoading || loading}
                  className="w-full bg-gradient-to-r from-[#10c9c0] to-[#0eb1a9] text-white py-5 px-8 rounded-lg font-semibold hover:from-[#0eb1a9] hover:to-[#0da399] transition-all duration-300 shadow-lg disabled:opacity-50 text-lg"
                >
                  {formLoading || loading ? (
                    <>
                      Creating Account...
                    </>
                  ) : (
                    <>
                      Create Agent Account
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-8 lg:px-12 py-6 text-center border-t border-gray-100">
            <p className="text-sm text-gray-600">
              Need help? Contact our support team at{' '}
              <a href="mailto:info@akiya.com" className="text-[#10c9c0] hover:underline">
                info@akiya.com
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Tag Input Component
const TagInput = ({ items, onAdd, onRemove, placeholder }) => {
  const [inputValue, setInputValue] = useState('');

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      e.preventDefault();
      onAdd(inputValue);
      setInputValue('');
    }
  };

  const handleAddClick = () => {
    if (inputValue.trim()) {
      onAdd(inputValue);
      setInputValue('');
    }
  };

  return (
    <div>
      <div className="flex flex-wrap gap-3 mb-6">
        {items.map((item, index) => (
          <span
            key={index}
            className="inline-flex items-center px-4 py-2 bg-[#10c9c0]/10 text-[#0eb1a9] rounded-full text-sm font-medium"
          >
            {item}
            <button
              type="button"
              onClick={() => onRemove(index)}
              className="ml-2 text-[#0eb1a9] hover:text-red-600 transition-colors text-lg font-bold"
            >
              ×
            </button>
          </span>
        ))}
      </div>
      
      <div className="flex space-x-3">
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder={placeholder}
          className="flex-1 p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#10c9c0] focus:border-transparent"
        />
        <button
          type="button"
          onClick={handleAddClick}
          className="px-6 py-4 bg-[#10c9c0] text-white rounded-lg hover:bg-[#0eb1a9] transition-colors font-semibold"
        >
          <FaPlus />
        </button>
      </div>
      
      <p className="text-sm text-gray-500 mt-3">
        Type and press Enter or click Add Item to include new entries
      </p>
    </div>
  );
};

export default AgentRegistration;